/**
 * Plain-English names for `outreach_recipients.skip_reason`.
 *
 * Why this exists (2026-09-02, Don Elwell). The approval queue had a label map
 * covering six reasons and fell through to `?? skipReason` for the rest, so the
 * consent reasons printed the raw database token. Don spent a stretch of the
 * 9/1 call reading "sms_no_explicit_consent" off his screen and reasonably
 * hearing *they told us not to text them* — when it means nobody at the
 * dealership ever ticked the box. His words: "I would probably have it say opt
 * out or something along those lines instead of no explicit consent."
 *
 * So the distinction the wording has to carry is **refusal vs absence of a
 * record**. "Opted out" is a person saying no. "No consent recorded" is a blank
 * field. They lead to opposite decisions and they must not read alike.
 *
 * Two things beyond the map itself:
 *
 *  - **Composite reasons.** planOutreachChannels() joins reasons with "+" when
 *    several channels are blocked at once (`email_no_postal_address+sms_no_explicit_consent`).
 *    Split and label each part, or the composite misses the map and leaks the
 *    raw string again — the exact bug, one layer down.
 *  - **Unknown reasons humanise rather than passing through.** A reason added
 *    server-side and not listed here should read as clumsy English, never as a
 *    database token. That is what put this on a client call.
 */

const SKIP_REASON_LABEL: Record<string, string> = {
  // Eligibility — we chose not to ask.
  already_reviewed: "Already reviewed",
  cooldown: "Asked recently",
  cross_type_recent_ask: "Other campaign asked",
  duplicate_identity: "Same person, asked once",
  daily_cap: "Daily cap",
  manual: "Manually skipped",
  bought_after_visit: "Came back and bought",
  // The CRM record behind an unsold visit vanished (merged, deleted, or its
  // source_record_id drifted) and never came back before the visit aged out of
  // the campaign's recency window. Nobody wants "how was your visit" five weeks
  // late, so the row is retired rather than retried forever.
  visit_too_old: "Visit too old to ask",
  // Consent and compliance — we were not allowed to ask on that channel.
  sms_opted_out: "Opted out",
  sms_no_explicit_consent: "No consent recorded",
  email_no_postal_address: "No mailing address on file",
  suppressed: "Suppressed",
};

/** Sentence-case a token we have no label for: "some_new_reason" → "Some new reason". */
function humanise(token: string): string {
  const words = token.replace(/_/g, " ").trim();
  return words.charAt(0).toUpperCase() + words.slice(1);
}

/**
 * Display name for a skip reason, including the "+"-joined composites.
 * Returns null for null/empty so callers can skip rendering entirely.
 */
export function skipReasonLabel(raw: string | null | undefined): string | null {
  const value = (raw ?? "").trim();
  if (!value) return null;
  return value
    .split("+")
    .map((part) => part.trim())
    .filter(Boolean)
    .map((part) => SKIP_REASON_LABEL[part] ?? humanise(part))
    .join(" + ");
}
